import { useCallback, useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"

import {
  AttributionDateRange,
  ChannelExpense,
  ChannelStat,
  InfluencerRecord,
} from "./types"
import { enrichStatsWithInfluencers } from "./utils"
import type { AttributionModelKey } from "@/lib/attribution/attribution-models"

interface UseInfluencerDataOptions {
  integrationId: string
  configRevision: number
}

interface UseInfluencerDataResult {
  influencers: InfluencerRecord[]
  refreshInfluencers: () => Promise<void>
}

export const useInfluencerData = ({
  integrationId,
  configRevision,
}: UseInfluencerDataOptions): UseInfluencerDataResult => {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [influencers, setInfluencers] = useState<InfluencerRecord[]>([])

  const refreshInfluencers = useCallback(async () => {
    if (!integrationId || !isAuthenticated) {
      setInfluencers([])
      return
    }

    try {
      const response = await fetchWithAuth(
        `/api/influencers?integration_id=${integrationId}&include_archived=true&config_revision=${configRevision}`,
      )
      if (!response.ok) {
        console.error("[Attribution Dashboard] Failed to fetch influencers", response.status)
        return
      }

      const data = await response.json()
      const normalized: InfluencerRecord[] = (data.influencers || []).map((influencer: InfluencerRecord) => ({
        ...influencer,
        codes: (influencer.codes || [])
          .filter(Boolean)
          .map((code) => ({
            code: code.code.trim().toUpperCase(),
            created_at: code.created_at ?? null,
            archived_at: code.archived_at ?? null,
          })),
      }))

      setInfluencers(normalized)
    } catch (error) {
      console.error("[Attribution Dashboard] Error loading influencers:", error)
    }
  }, [fetchWithAuth, integrationId, isAuthenticated])

  useEffect(() => {
    void refreshInfluencers()
  }, [refreshInfluencers, configRevision, isAuthenticated])

  return { influencers, refreshInfluencers }
}

interface UseAttributionStatsOptions {
  integrationId: string
  dateRange: AttributionDateRange
  configRevision: number
  influencers: InfluencerRecord[]
  attributionModel: AttributionModelKey
  minimumOrderTotal: number
  productIds?: number[]
}

interface UseAttributionStatsResult {
  stats: ChannelStat[]
  expenses: ChannelExpense[]
  isLoading: boolean
  refresh: () => Promise<void>
}

export const useAttributionStats = ({
  integrationId,
  dateRange,
  configRevision,
  influencers,
  attributionModel,
  minimumOrderTotal,
  productIds = [],
}: UseAttributionStatsOptions): UseAttributionStatsResult => {
  const { fetchWithAuth, isAuthenticated } = useAuth()
  const [stats, setStats] = useState<ChannelStat[]>([])
  const [expenses, setExpenses] = useState<ChannelExpense[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const influencersRef = useRef<InfluencerRecord[]>(influencers)
  useEffect(() => {
    influencersRef.current = influencers
    setStats((previous) => enrichStatsWithInfluencers(previous, influencers))
  }, [influencers])

  const refresh = useCallback(async () => {
    if (!integrationId || !isAuthenticated) {
      setStats([])
      setExpenses([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)

    try {
      const params = new URLSearchParams({
        integration_id: integrationId,
        month_start_utc: dateRange.month_start_utc.toISOString(),
        month_end_utc: dateRange.month_end_utc.toISOString(),
        timezone: dateRange.timezone,
      })
      params.set("config_revision", String(configRevision))
      params.set("attribution_model", attributionModel)
      params.set("minimum_order_total", Number.isFinite(minimumOrderTotal) ? String(minimumOrderTotal) : "0")

      if (productIds.length > 0) {
        params.set("product_ids", productIds.join(","))
      }

      const [statsRes, expensesRes] = await Promise.all([
        fetchWithAuth(`/api/integrations/shopify/attribution-stats?${params}`),
        fetchWithAuth(`/api/integrations/shopify/channel-expenses?${params}`),
      ])

      if (statsRes.ok) {
        const data = await statsRes.json()
        const enriched = enrichStatsWithInfluencers(data.stats || [], influencersRef.current)
        setStats(enriched)
      } else {
        setStats([])
      }

      if (expensesRes.ok) {
        const data = await expensesRes.json()
        setExpenses(data.expenses || [])
      } else {
        setExpenses([])
      }
    } catch (error) {
      console.error("[Attribution Dashboard] Error fetching data:", error)
    } finally {
      setIsLoading(false)
    }
  }, [
    integrationId,
    dateRange.month_start_utc,
    dateRange.month_end_utc,
    dateRange.timezone,
    configRevision,
    attributionModel,
    minimumOrderTotal,
    productIds,
    isAuthenticated,
    fetchWithAuth,
  ])

  useEffect(() => {
    void refresh()
  }, [refresh, isAuthenticated])

  return {
    stats,
    expenses,
    isLoading,
    refresh,
  }
}
